import * as React from 'react';

import classnames from 'classnames';
import {Table, Column} from 'fixed-data-table-2';
import * as ramda from 'ramda';
import _ from 'underscore';

import type {EntityListType, FilterObject} from 'nutshell-core/types';

// @ts-expect-error
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {RelativeOverlay} from 'shells/relative-overlay/relative-overlay';

import {ColumnPickerButton} from '../column-picker/column-picker-button';
import {NutCell} from '../columns/cell';
import {RIGHT_ALIGNED_TYPES} from '../columns/cell/string';
// @ts-expect-error
import {EntityTableHeader} from '../columns/entity-table-header';

import {AudienceIsBuildingView} from './audience-is-building-view';
import {TableEmptyView} from './table-empty-view';
// @ts-expect-error
import {TableErrorView} from './table-error-view';
import {TableNoResultsView} from './table-no-results-view';
// @ts-expect-error
import TouchableWrapper from './touchable-wrapper';

import styles from './entity-table.module.css';

import 'fixed-data-table-2/dist/fixed-data-table.css';

const nameMinWidth = 200;
const nameDefaultWidth = 300;
const minWidth = 100;
const maxWidth = 800;
const columnPickerButtonWidth = 142;

// @ts-expect-error
function defaultColumnWidth(size) {
    switch (size) {
        case 'sm':
            return 100;
        case 'lg':
            return 200;
        default:
            return 140; // Fits September 20, 2015
    }
}

export type Row = any;
export type ColumnWidths = {
    [key: string]: number;
};

type Props = {
    entityType: EntityListType;
    schema: any;
    columns: {
        selected: any;
        setFields: () => any;
        resetToDefault: () => any;
    };
    filters: {
        selected: FilterObject[];
        removeAllFilters: () => void;
    };
    sort: any;
    onToggleSort: () => void;
    rowHeight?: number;
    height: number;
    width: number;
    getRow: (index: number) => Row;
    checkRowSelected: (entityId: string) => boolean;
    toggleItem?: (rowId: string) => void;
    shiftSelectItem?: (rowId: string) => void;
    startScrolling: () => void;
    stopScrolling: () => void;
    selectAllItems?: () => void;
    isFiltering: boolean;
    isLoading: boolean;
    isFirstLoad: boolean;
    isAllSelected: boolean;
    hasError: boolean;
    rowCount: number;
    unfilteredCount: number;
    unfilteredCountThreshold: number;
    onResizeColumn?: (columnWidths: ColumnWidths) => void;
    includeColumnPicker?: boolean;
    customEmptyStateComponent?: React.ReactNode;
    scrollToColumnIndex?: number;
    handleRowClick?: (rowId: string) => void;
    audienceId?: string | null | undefined;
};

type State = {
    columnWidths: ColumnWidths;
    isColumnResizing: boolean;
    numFilterPopoversOpen: number;
    contentWidth: number;
    scrollTop: number;
};

export class EntityTable extends React.Component<Props, State> {
    static defaultProps = {
        rowHeight: 45,
        headerHeight: 34,
        includeColumnPicker: true,
        unfilteredCountThreshold: 5,
    };

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const nextColumns = nextProps.columns.selected;
        const prevColumns = this.props.columns.selected;
        if (nextColumns !== prevColumns || nextProps.schema !== this.props.schema) {
            this.setState({contentWidth: calculateColumnWidths(nextColumns, nextProps.schema)});
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.isFirstLoad && !this.props.isFirstLoad) {
            this.setState({
                // @ts-expect-error
                scrollTop: undefined,
            });
        }
    }

    constructor(props: Props) {
        super(props);
        // Pull a number out of the hash or default to 0 if undefined
        // @ts-expect-error
        const startRow = window.location.hash.match(/\d+/) | 0;
        this.state = {
            columnWidths: getPersistedColumnWidths(props.entityType),
            isColumnResizing: false,
            numFilterPopoversOpen: 0,
            contentWidth: 0,
            scrollTop: props.rowHeight ? props.rowHeight * startRow : 0,
        };
    }

    mapColumns(includeColumnPicker: boolean) {
        const {
            schema,
            columns: {selected: columns},
        } = this.props;

        if (!Object.keys(schema).length) return [];
        // @ts-expect-error
        const columnsInSchema = columns.filter((columnName) => schema[columnName]);

        // @ts-expect-error
        return columnsInSchema.map((columnName, index) => {
            const size = schema[columnName].size;
            const isName = columnName === 'name';
            const extraWidth =
                includeColumnPicker && index === columnsInSchema.length - 1
                    ? columnPickerButtonWidth
                    : 0;

            return (
                <Column
                    key={columnName}
                    columnKey={columnName}
                    fixed={isName}
                    minWidth={isName ? nameMinWidth : minWidth}
                    maxWidth={maxWidth}
                    width={
                        extraWidth +
                        (this.state.columnWidths[columnName] ||
                            (isName ? nameDefaultWidth : defaultColumnWidth(size)))
                    }
                    isResizable={this.state.numFilterPopoversOpen === 0}
                    header={
                        <EntityTableHeader
                            fieldName={columnName}
                            entityType={this.props.entityType}
                            schema={this.props.schema}
                            sort={this.props.sort}
                            onToggleSort={this.props.onToggleSort}
                            fields={this.props.columns}
                            filters={this.props.filters}
                            selectAllItems={this.props.selectAllItems}
                            isAllSelected={this.props.isAllSelected}
                            width={
                                extraWidth +
                                (this.state.columnWidths[columnName] ||
                                    (isName ? nameDefaultWidth : defaultColumnWidth(size)))
                            }
                            widthAdjustment={extraWidth}
                            onPopoverOpen={this.handlePopoverOpen}
                            onPopoverClose={this.handlePopoverClosed}
                        />
                    }
                    cell={this.generateNutCell(extraWidth)}
                />
            );
        });
    }

    render() {
        const {isFiltering, isFirstLoad, unfilteredCount, hasError, rowCount} = this.props;

        return (
            <div className='ui-entity-list__table-wrapper'>
                {
                    /* Delay rendering of table until we have loaded data. This
                     *  allows us to jump to the users last scroll position on
                     *  back button clicks.
                     */
                    isFirstLoad ? (
                        <div style={{width: this.props.width, height: this.props.height}} />
                    ) : (
                        <div style={{position: 'relative'}}>
                            <TouchableWrapper
                                contentWidth={this.state.contentWidth}
                                height={this.props.height}
                                width={this.props.width}
                            >
                                {/* @ts-expect-error */}
                                <Table
                                    rowClassNameGetter={this.getRowClassName}
                                    rowsCount={rowCount}
                                    keyboardScrollEnabled={true}
                                    keyboardPageEnabled={true}
                                    isColumnResizing={this.state.isColumnResizing}
                                    onColumnResizeEndCallback={this.handleColumnResizeEndCallback}
                                    onRowClick={this.handleRowClick}
                                    scrollTop={this.state.scrollTop}
                                    scrollToRow={isFiltering ? 0 : undefined}
                                    onScrollStart={this.handleScrollStart}
                                    onScrollEnd={this.handleScrollEnd}
                                    scrollToColumn={
                                        this.props.scrollToColumnIndex === -1
                                            ? this.props.columns.selected.length - 1
                                            : 0
                                    }
                                    {...this.props}
                                >
                                    {this.mapColumns(Boolean(this.props.includeColumnPicker))}
                                </Table>
                            </TouchableWrapper>
                            {this.props.includeColumnPicker ? (
                                <div className={styles['column-picker-button']}>
                                    <ColumnPickerButton
                                        entityType={this.props.entityType}
                                        schema={_.values(this.props.schema)}
                                        // @ts-expect-error
                                        iconOnly={true}
                                        onResetFieldsToDefault={this.props.columns.resetToDefault}
                                        onUpdateFields={this.props.columns.setFields}
                                        selectedColumns={this.props.columns.selected}
                                    />
                                </div>
                            ) : undefined}
                        </div>
                    )
                }

                {hasError ? (
                    <RelativeOverlay isOpaque={true}>
                        <TableErrorView />
                    </RelativeOverlay>
                ) : undefined}
                {this.state.numFilterPopoversOpen > 0 ? <RelativeOverlay /> : undefined}
                {isFiltering && !hasError ? (
                    <RelativeOverlay>
                        <div className='ui-entity-list__loading-state'>
                            <LoadingIcon />
                        </div>
                    </RelativeOverlay>
                ) : undefined}
                {this.props.audienceId && this.isAudienceBuilding() ? (
                    <AudienceIsBuildingView
                        audienceId={this.props.audienceId}
                        currentCount={this.props.rowCount}
                    />
                ) : undefined}
                {this.noFilteredResults() && !hasError ? (
                    <TableNoResultsView removeFilters={this.props.filters.removeAllFilters} />
                ) : undefined}
                {this.isEmptyState() && !hasError
                    ? this.renderEmptyState(unfilteredCount)
                    : undefined}
            </div>
        );
    }

    renderEmptyState = (unfilteredCount: number) => {
        if (this.props.customEmptyStateComponent) {
            return (
                <div
                    className={classnames(
                        'absolutely-center-and-fill',
                        styles['custom-empty-state-overlay']
                    )}
                >
                    {this.props.customEmptyStateComponent}
                </div>
            );
        }

        return (
            <TableEmptyView
                type={this.props.entityType}
                tableHeight={this.props.height}
                unfilteredCount={unfilteredCount}
            />
        );
    };

    noFilteredResults() {
        const {
            unfilteredCount,
            unfilteredCountThreshold,
            isFiltering,
            isLoading,
            hasError,
            rowCount,
            filters,
        } = this.props;

        if (!filters) return false;

        return (
            rowCount === 0 &&
            unfilteredCount !== 0 &&
            (unfilteredCount > unfilteredCountThreshold || filters.selected.length) &&
            !isLoading &&
            !isFiltering &&
            !hasError
        );
    }

    isAudienceBuilding() {
        if (!this.props.audienceId || this.props.entityType !== 'contacts') {
            return false;
        }

        const isFilteringAudience =
            this.props.filters.selected.length === 1 &&
            // @ts-expect-error
            this.props.filters.selected[0].audiences &&
            // @ts-expect-error
            this.props.filters.selected[0].audiences.data &&
            // @ts-expect-error
            this.props.filters.selected[0].audiences.data[0] &&
            // @ts-expect-error
            this.props.filters.selected[0].audiences.data[0].data === this.props.audienceId;

        return isFilteringAudience && this.noFilteredResults() && !this.props.hasError;
    }

    isEmptyState() {
        const {
            unfilteredCount,
            unfilteredCountThreshold,
            isFiltering,
            isLoading,
            hasError,
            filters,
        } = this.props;

        return (
            unfilteredCount === 0 ||
            ((unfilteredCount <= unfilteredCountThreshold ||
                (typeof unfilteredCount === 'undefined' && this.props.rowCount === 0)) &&
                !isLoading &&
                !isFiltering &&
                !hasError &&
                filters &&
                filters.selected &&
                !filters.selected.length)
        );
    }

    // FixedDataTable sends us cellData in the form of an object, containing
    // columnKey, height, width, and rowIndex
    generateNutCell = (widthOffset: number) => {
        return (cellData: any) => {
            const {columnKey, height, width, rowIndex} = cellData;

            // If the column is right-aligned, we need to subtract the width offset
            // otherwise the data may appear too far to the right (relative to the column header)
            const type = this.props.schema[columnKey].type;
            const renderWidth = RIGHT_ALIGNED_TYPES.includes(type) ? width - widthOffset : width;

            return (
                <NutCell
                    rowIndex={rowIndex}
                    height={height}
                    width={renderWidth}
                    field={columnKey}
                    getRow={this.props.getRow}
                    toggleItem={this.props.toggleItem}
                    shiftSelectItem={this.props.shiftSelectItem}
                    checkRowSelected={this.props.checkRowSelected}
                    schema={this.props.schema}
                />
            );
        };
    };

    getRowClassName = (index: number) => {
        const row = this.props.getRow(index);
        const isSelected = this.props.checkRowSelected(row.id);

        if (row) {
            return classnames({
                'public_fixedDataTableRow_main--deleted': row.isDeleted,
                'public_fixedDataTableRow_main--selected': isSelected,
                'cursor-pointer': typeof this.props.handleRowClick === 'function',
            });
        }

        return null;
    };

    handleRowClick = (event: React.KeyboardEvent<any>, index: number) => {
        if (typeof this.props.handleRowClick === 'function') {
            const row = this.props.getRow(index);

            this.props.handleRowClick(row.id);
        } else if (typeof this.props.toggleItem === 'function') {
            const row = this.props.getRow(index);

            // Don't allow for text selection if we're selecting a row
            //
            // https://stackoverflow.com/a/1527797
            if (event.nativeEvent.shiftKey && this.props.shiftSelectItem) {
                document.body.onselectstart = function () {
                    return false;
                };

                this.props.shiftSelectItem(row.id);

                // Unselect the (potential) shift-selected range of text
                // that's native to browsers, if we got one
                //
                // @ts-expect-error
                document.getSelection().removeAllRanges();

                // reset onselectstart
                document.body.onselectstart = function () {
                    return true;
                };
            } else if (typeof this.props.toggleItem === 'function') {
                // recheck for flow
                this.props.toggleItem(row.id);
            }
        }
    };

    handlePopoverOpen = () => {
        this.setState((prevState) => ({
            numFilterPopoversOpen: prevState.numFilterPopoversOpen + 1,
        }));
    };

    handlePopoverClosed = () => {
        // Shouldn't ever end up in a '<= 0' state, but ¯\_(ツ)_/¯
        if (this.state.numFilterPopoversOpen > 0) {
            this.setState((prevState) => ({
                numFilterPopoversOpen: prevState.numFilterPopoversOpen - 1,
            }));
        }
    };

    handleScrollStart = () => {
        this.props.startScrolling();
    };

    // This needs to be debounced, because mobile safari limits to 100 every 30 secs.
    // And apparently it also calls `onScrollEnd` more often than that.
    // @ts-expect-error
    handleScrollEnd = _.debounce((x, y) => {
        this.props.stopScrolling();
        const {pathname, search} = window.location;
        const row = this.props.rowHeight ? Math.floor(y / this.props.rowHeight) : 0;

        // @ts-expect-error
        history.replaceState({}, undefined, `${pathname}${search}#${row}`);
    }, 350);

    handleColumnResizeEndCallback = (width: number, key: string) => {
        this.setState((prevState, props) => {
            const columnWidths = ramda.mergeRight(prevState.columnWidths, {[key]: width});

            Nut.writeLocalStorage(
                `${props.entityType}PersistedColumnWidths`,
                JSON.stringify(columnWidths)
            );

            sendResizeColumnAnalytics(width, key);

            if (props.onResizeColumn) {
                props.onResizeColumn(columnWidths);
            }

            return {
                columnWidths,
                isColumnResizing: false,
            };
        });
    };
}

/*
 * Helper functions
 */

// @ts-expect-error
function calculateColumnWidths(columns, schema) {
    const totalColumnWidths = columns
        // @ts-expect-error
        .filter((column) => column !== 'name') // We start with name's default width of 350
        // @ts-expect-error
        .filter((column) => ramda.contains(column, schema))
        // @ts-expect-error
        .reduce((acc, column) => {
            const {size} = schema[column];

            return acc + defaultColumnWidth(size);
        }, nameDefaultWidth);

    return totalColumnWidths;
}

// @ts-expect-error
function getPersistedColumnWidths(entityType) {
    const persistedColumnWidths =
        Nut.readLocalStorage(`${entityType}PersistedColumnWidths`) || '{}';

    return JSON.parse(persistedColumnWidths);
}

// @ts-expect-error
function sendResizeColumnAnalytics(width, key) {
    // Rounding down to nearest multiple of "50" for sane analytics
    const roundedWidth = Math.floor(width / 50) * 50;
    const eventProps = {
        column: key,
        width: roundedWidth,
    } as const;

    Analytics.trackEvent(
        Analytics.categories.filtering,
        Analytics.actions.filtering.didResizeColumn,
        eventProps
    );
}
